/**
 * Turns visible jobs + schedule groups into a flat list of “rows” for the left panel and
 * grid: one project row (optionally expanded) then nested group rows with sort order and
 * expand state. Consumed by {@link ScheduleProjectsPanel} and {@link TimelineCanvas}.
 *
 * @typedef {Object} ScheduleFlatRow
 * @property {'project'|'group'} rowType
 * @property {string} id Stable row key for React lists
 * @property {string} projectId
 * @property {number} depth 0 = project, 1+ = nested groups
 * @property {string} label
 * @property {string|null} [color] Hex color for group rows
 * @property {string|null} [groupId] Present when rowType === 'group'
 * @property {boolean} hasChildren Whether this row can expand (nested groups under project, or child groups)
 */

/**
 * Index schedule groups by project id and sort each project bucket by group order.
 *
 * @param {Array<Object>} groups
 * @returns {Map<unknown, Array<Object>>}
 */
function groupGroupsByProjectId(groups) {
  const map = new Map();
  for (const g of groups) {
    if (!map.has(g.projectId)) {
      map.set(g.projectId, []);
    }
    map.get(g.projectId).push(g);
  }
  for (const list of map.values()) {
    list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }
  return map;
}

/**
 * Direct child groups of `parentId` within one project's group list.
 *
 * @param {string|undefined} parentId Parent group id, or undefined for roots.
 * @param {Array<Object>} allForProject Groups belonging to one project.
 * @returns {Array<Object>}
 */
function getChildGroups(parentId, allForProject) {
  return allForProject.filter((g) => g.parentGroupId === parentId);
}

/**
 * Top-level groups for a project (groups with no parent group id).
 *
 * @param {Array<Object>} allForProject
 * @returns {Array<Object>}
 */
function getRootGroups(allForProject) {
  return allForProject.filter((g) => !g.parentGroupId);
}

/**
 * Sort projects by displayed project number with numeric-aware compare.
 *
 * @param {Array<Object>} projects
 * @returns {Array<Object>}
 */
function sortProjects(projects) {
  return [...projects].sort((a, b) =>
    String(a.projectNumber).localeCompare(String(b.projectNumber), undefined, {
      numeric: true,
    })
  );
}

/**
 * Stable row id for a project row.
 *
 * @param {string|number} projectId Job / project id.
 * @returns {string}
 */
export function getScheduleProjectRowId(projectId) {
  return `project-${projectId}`;
}

/**
 * Stable row id for a schedule group row.
 *
 * @param {string|number} groupId Schedule group id.
 * @returns {string}
 */
export function getScheduleGroupRowId(groupId) {
  return `group-${groupId}`;
}

/**
 * Stable row id for the row that owns a WorkScopeAspectsBlock-shaped block.
 *
 * @param {Object} block Block with `job.id` and optional `scheduleGroup.id`.
 * @returns {string}
 */
export function getScheduleBlockRowId(block) {
  const groupId = block.scheduleGroup?.id ?? null;
  const jobId = block.job?.id ?? null;
  return groupId ? getScheduleGroupRowId(groupId) : getScheduleProjectRowId(jobId);
}

/**
 * Flatten projects + nested groups into visible timeline rows (read-only tree walker).
 *
 * @param {Object} params
 * @param {Array} params.projects
 * @param {Array} params.groups
 * @param {Set<string>} params.expandedProjectIds
 * @param {Set<string>} params.expandedGroupIds
 * @returns {ScheduleFlatRow[]}
 */
export function flattenVisibleScheduleRows({
  projects,
  groups,
  expandedProjectIds,
  expandedGroupIds,
}) {
  const byProject = groupGroupsByProjectId(groups);
  /** @type {ScheduleFlatRow[]} */
  const rows = [];

  for (const project of sortProjects(projects)) {
    const projGroups = byProject.get(project.id) || [];
    const roots = getRootGroups(projGroups);
    const hasChildren = roots.length > 0;

    rows.push({
      rowType: "project",
      id: getScheduleProjectRowId(project.id),
      projectId: project.id,
      depth: 0,
      label: `${project.projectNumber} — ${project.name}`,
      color: null,
      hasChildren,
    });

    if (!expandedProjectIds.has(project.id)) {
      /* eslint-disable-next-line no-continue */
      continue;
    }

    /**
     * Flatten one expanded group subtree into left-rail rows (depth-first).
     *
     * @param {Object} group Schedule group node.
     * @param {number} depth Indent level under the project row.
     * @returns {void}
     */
    const walkGroup = (group, depth) => {
      const children = getChildGroups(group.id, projGroups).sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0)
      );
      const hasChildGroups = children.length > 0;

      rows.push({
        rowType: "group",
        id: getScheduleGroupRowId(group.id),
        projectId: project.id,
        groupId: group.id,
        depth,
        label: group.name,
        color: group.color ?? null,
        hasChildren: hasChildGroups,
      });

      if (hasChildGroups && expandedGroupIds.has(group.id)) {
        for (const child of children) {
          walkGroup(child, depth + 1);
        }
      }
    };

    for (const root of roots) {
      walkGroup(root, 1);
    }
  }

  return rows;
}
